<template>
    <div :id="`cropper-${uid}`">
        <img ref="cropperImage" :src="file" alt=""/>
    </div>
</template>

<script setup>
import Cropper from 'cropperjs';
import "cropperjs/dist/cropper.css";
import { ref, onMounted } from "vue";
import { v4 as uuidv4 } from "uuid";

const props = defineProps({
    file: {
        type: String,
        required: true,
    },
    model: {
        type: Object,
        required: true,
    },
    aspectRatio: {
        type: Number,
        required: false,
        default: 1,
    },
    dynamicPreview: Boolean,
});

const uid = uuidv4();
const emit = defineEmits(['loaded']);
const cropper = ref(null);
const cropperImage = ref(null);
let previewReady = false;

const reset = (cropPos) => {
    cropper.value.setCropBoxData(cropPos);
};

onMounted(() => {
    const previewElement = document.getElementById('cropper-preview')

    cropper.value = new Cropper(cropperImage.value, {
        viewMode: 2,
        dragMode: 'move',
        aspectRatio: props.aspectRatio,
        autoCropArea: 1,
        restore: false,
        zoomable: false,
        movable: false,
        guides: false,
        center: false,
        highlight: false,
        cropBoxMovable: true,
        cropBoxResizable: false,
        toggleDragModeOnDblclick: false,

        ready: function () {
            const clone = this.cloneNode();

            clone.className = '';
            clone.style.cssText = (
                'display: block;' +
                'width: 100%;' +
                'min-width: 0;' +
                'min-height: 0;' +
                'max-width: none;' +
                'max-height: none;' +
                'height: 233px;' +
                'width: 216px;'
            );

            if (previewElement) {
                previewElement.appendChild(clone.cloneNode());
            }

            if (props.model.crop_position) {
                this.cropper.setCropBoxData(props.model.crop_position.cropper);
            } else {
                this.cropper.setCropBoxData({ left: 0, top: 0 });
            }

            previewReady = true;
            emit('loaded');
        },

        crop: function (event) {
            if (! previewReady) {
                return;
            }

            const data = event.detail;
            const cropper = this.cropper;
            const imageData = cropper.getImageData();

            if (previewElement) {
                previewElement.classList.remove('cropper-initial');
                const previewWidth = 216;
                const previewHeight = 233;

                const imageScaledRatio = data.width / previewWidth;
                const isPortrait = imageData.naturalHeight > imageData.naturalWidth;

                const marginTop = -data.y / imageScaledRatio;
                const marginLeft = -data.x / imageScaledRatio;

                let maskLeft = 0;
                if (! isPortrait) {
                    maskLeft = (marginLeft * -1) + 3;
                }

                let maskTop = 0;
                if (isPortrait) {
                    maskTop = marginTop * - 1;
                }

                const previewImage = previewElement.getElementsByTagName('img').item(0);

                previewElement.style.height = previewHeight + 'px';
                previewImage.style.width = imageData.naturalWidth / imageScaledRatio + 'px';
                previewImage.style.height = '233px';
                previewImage.style.marginLeft = marginLeft + 'px';
                previewImage.style.marginTop = marginTop + 'px';
                previewImage.style.maskPosition = maskLeft + 'px ' + maskTop + 'px';
                previewImage.style.position = 'relative';
                previewImage.style.left = '-2px';
                previewImage.style.top = '1px';
            }

            const style = getComputedStyle(cropper.cropBox);
            const matrix = new WebKitCSSMatrix(style.transform);

            const translateY = matrix.m42;
            const translateX = matrix.m41;
            const imageHeight = imageData.height;
            const imageWidth = imageData.width;

            props.model.crop_position = {
                percentage: {
                    top: translateY / imageHeight * 100,
                    left: translateX / imageWidth * 100,
                },
                cropper: {
                    top: cropper.cropBoxData.top,
                    left: cropper.cropBoxData.left,
                }
            }
        },
    });
});

defineExpose({
    reset
});
</script>
